import { useEffect, useRef } from 'react';
import { getAccessToken, reactiveRefresh, getOrCreateDeviceId } from '@/lib/authToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

type HeartbeatOptions = {
  enabled?: boolean;
  intervalMs?: number; // base interval
  jitterMs?: number;   // max extra jitter per tick
};

function isActiveTab() {

    
  if (typeof document === 'undefined') return true;
  const visible = document.visibilityState === 'visible';
  // document.hasFocus can be false when devtools focused; we still treat as active if visible
  const focused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
  return visible && focused;
}

/**
 * Sends a lightweight heartbeat to backend to mark user/session as active now.
 * - Includes cookies (credentials: include)
 * - Adds X-Device-Id for device-level identification
 * - If 401 with a token, tries a one-time reactive refresh, then retries once
 * - Ignores non-OK responses (diagnostic only)
 */
async function postHeartbeat(signal?: AbortSignal): Promise<void> {
  const deviceId = getOrCreateDeviceId();
  const token = getAccessToken();

  const url = `${API_BASE_URL}/api/user/heartbeat`;
  const headers: Record<string, string> = {
    'Accept': 'application/json, text/plain;q=0.8, */*;q=0.5',
    'Content-Type': 'application/json',
    'X-Device-Id': deviceId,
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ device_id: deviceId }),
    signal,
    keepalive: true,
  });

  // On 401 with token, try one refresh and retry once
  if (res.status === 401 && token) {
    const fresh = await reactiveRefresh();
    if (fresh) {
      headers['Authorization'] = `Bearer ${fresh}`;
      res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ device_id: deviceId }),
        signal,
        keepalive: true,
      });
    }
  }

  // Swallow non-OKs (401/403/400/5xx) silently; heartbeat is best-effort
  // Optionally log in dev for diagnostics
  if (!res.ok && import.meta.env.DEV) {
    try {
      const txt = await res.text();
      // eslint-disable-next-line no-console
      console.debug('[heartbeat] non-OK', res.status, txt.slice(0, 200));
    } catch { /* ignore */ }
  }
}

export function useHeartbeat(opts: HeartbeatOptions = {}) {
  const { enabled = true, intervalMs = 60_000, jitterMs = 10_000 } = opts;
  const timerRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  // Persist a per-tab jitter seed so multiple tabs don't align
  const jitterSeed = (() => {
    try {
      const key = 'hb_jitter_ms';
      const existing = sessionStorage.getItem(key);
      if (existing) return Math.max(0, Math.min(jitterMs, parseInt(existing, 10) || 0));
      const v = Math.floor(Math.random() * (jitterMs + 1));
      sessionStorage.setItem(key, String(v));
      return v;
    } catch {
      return Math.floor(Math.random() * (jitterMs + 1));
    }
  })();

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const clearTimer = () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const scheduleNext = () => {
      // add fresh small jitter each tick around the per-tab seed
      const micro = Math.floor(Math.random() * 1000); // 0..999ms
      const delay = intervalMs + jitterSeed + micro;
      timerRef.current = window.setTimeout(tick, delay) as any;
    };

    const tick = async () => {
      if (cancelled) return;
      if (!isActiveTab()) { scheduleNext(); return; }
      if (!navigator.onLine) { scheduleNext(); return; }
      if (inFlightRef.current) { scheduleNext(); return; }

      inFlightRef.current = true;
      abortRef.current = new AbortController();
      try {
        await postHeartbeat(abortRef.current.signal);
      } catch {
        // best-effort: ignore errors
      } finally {
        inFlightRef.current = false;
        abortRef.current = null;
      }
      scheduleNext();
    };

    // Immediate fire when becoming visible/active
    const fireNow = async () => {
      if (!enabled) return;
      if (!isActiveTab()) return;
      if (!navigator.onLine) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      abortRef.current = new AbortController();
      try {
        await postHeartbeat(abortRef.current.signal);
      } catch { /* ignore */ }
      finally {
        inFlightRef.current = false;
        abortRef.current = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fireNow();
        clearTimer();
        scheduleNext();
      } else {
        clearTimer();
      }
    };

    const onFocus = () => {
      fireNow();
    };

    const onOnline = () => {
      fireNow();
    };

    const onPageHide = () => {
      // Do NOT use sendBeacon here: backend requires Authorization header,
      // and beacon cannot send headers → results in noisy 401 logs.
      // Instead, just cancel any in-flight request.
      try { abortRef.current?.abort(); } catch { /* ignore */ }
    };

    // Start loop only if tab is visible
    if (isActiveTab()) {
      fireNow();
      scheduleNext();
    }

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);

    return () => {
      cancelled = true;
      clearTimer();
      try { abortRef.current?.abort(); } catch { /* ignore */ }
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
    };
  }, [enabled, intervalMs, jitterMs]);
}

export default useHeartbeat;
